import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import cx from 'classnames';
import {t} from 'i18next';
import {LexicalEditor} from 'lexical';
import {useCallback, useEffect, useState} from 'react';

import {$isEnhancedHeadingNode, EnhancedHeadingNode} from './EnhancedHeadingNode';
import {findHeadingNodeByHeadingId} from './HeadingNodeUtils';
import {useIsDocumentMode} from './RoleContext';
import {getSectionWordCount} from './SectionRangeUtils';

interface WordCountWatcherProps {
  headingId: string;
  onWordCountChange?: (count: number, isOverLimit: boolean) => void;
  className?: string;
}

export const WordCountWatcher = ({
  headingId,
  onWordCountChange,
  className,
}: WordCountWatcherProps) => {
  const [editor] = useLexicalComposerContext();
  const [wordCount, setWordCount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const isDocumentMode = useIsDocumentMode();

  const updateWordCount = useCallback(() => {
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);

      if (!(node instanceof HeadingNode)) {
        return;
      }

      const count = getSectionWordCount(editor, node);

      setWordCount(count);

      // Check if we have a word limit
      let overLimit = false;
      if ($isEnhancedHeadingNode(node)) {
        const wordLimit = node.getWordLimit();
        if (wordLimit && count > wordLimit) {
          overLimit = true;
        }
      }

      setIsOverLimit(overLimit);
      onWordCountChange?.(count, overLimit);
    });
  }, [editor, headingId, onWordCountChange]);

  useEffect(() => {
    // Initial count
    updateWordCount();

    // Listen for editor changes with a small delay to ensure state is fully updated
    const removeListener = editor.registerUpdateListener(() => {
      // Use setTimeout to ensure the editor state is fully committed
      setTimeout(() => {
        updateWordCount();
      }, 0);
    });

    return removeListener;
  }, [editor, headingId, updateWordCount]);

  if (!isDocumentMode) {
    return null;
  }

  const getDisplayText = () => {
    // Get word limit from the heading node
    let wordLimit: number | undefined;
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if ($isEnhancedHeadingNode(node)) {
        wordLimit = node.getWordLimit();
      }
    });

    if (wordLimit) {
      return `${wordCount} / ${wordLimit} ${t('word_count', {count: wordLimit})}`;
    }
    return `${wordCount} ${t('word_count', {count: wordCount})}`;
  };

  const getWordLimit = () => {
    let wordLimit: number | undefined;
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);
      if ($isEnhancedHeadingNode(node)) wordLimit = node.getWordLimit();
    });
    return wordLimit;
  };

  if (!getWordLimit()) {
    return null;
  }

  return (
    <Text
      className={cx(
        className,
        atoms({
          color: isOverLimit ? 'Alert Base' : 'Text Subtle',
        })
      )}
    >
      {getDisplayText()}
    </Text>
  );
};

/**
 * Hook to get word count for a specific heading
 */
export function useWordCount(headingId: string) {
  const [editor] = useLexicalComposerContext();
  const [wordCount, setWordCount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);

  const updateWordCount = useCallback(() => {
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);

      if (!(node instanceof HeadingNode)) {
        setWordCount(0);
        setIsOverLimit(false);
        return;
      }

      const count = getSectionWordCount(editor, node);

      // Debug logging
      console.log('useWordCount - updateWordCount:', {
        headingId,
        count,
        timestamp: Date.now(),
        nodeText: node.getTextContent().substring(0, 50) + '...',
      });

      setWordCount(count);

      // Check if we have a word limit
      let overLimit = false;
      if ($isEnhancedHeadingNode(node)) {
        const wordLimit = node.getWordLimit();
        if (wordLimit && count > wordLimit) {
          overLimit = true;
        }
      }

      setIsOverLimit(overLimit);
    });
  }, [editor, headingId]);

  useEffect(() => {
    updateWordCount();

    const removeUpdateListener = editor.registerUpdateListener(() => {
      // Use setTimeout to ensure the editor state is fully committed
      setTimeout(() => {
        updateWordCount();
      }, 0);
    });

    // Also listen for specific node changes to catch word limit updates
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, (node) => {
      if (node.getHeadingId() === headingId) {
        // Use setTimeout for node transforms as well
        setTimeout(() => {
          updateWordCount();
        }, 0);
      }
    });

    return () => {
      removeUpdateListener();
      removeNodeTransform();
    };
  }, [editor, headingId, updateWordCount]);

  return {wordCount, isOverLimit, updateWordCount};
}

/**
 * Command guard to prevent insertion when over word limit
 */
export function createWordLimitCommandGuard(
  editor: LexicalEditor,
  headingId: string
): (payload: any) => boolean {
  return (payload: any) => {
    let result = true;

    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);

      if (!(node instanceof EnhancedHeadingNode)) {
        result = true; // Allow if not a collapsible heading
        return;
      }

      const wordLimit = node.getWordLimit();
      if (!wordLimit) {
        result = true; // Allow if no word limit set
        return;
      }

      const currentCount = getSectionWordCount(editor, node);

      // Allow if under limit
      if (currentCount < wordLimit) {
        result = true;
        return;
      }

      // Check if this is a text insertion that would exceed the limit
      if (payload && typeof payload === 'string') {
        const wordsToAdd = payload
          .trim()
          .split(/\s+/)
          .filter((word: string) => word.length > 0).length;
        result = currentCount + wordsToAdd <= wordLimit;
        return;
      }

      // For other operations, be more permissive
      result = true;
    });

    return result;
  };
}

/**
 * Component to display word count with limit enforcement
 */
export const WordCountDisplay = ({
  headingId,
  showLimit = true,
  className,
}: {
  headingId: string;
  showLimit?: boolean;
  className?: string;
}) => {
  const {wordCount, isOverLimit} = useWordCount(headingId);
  const [editor] = useLexicalComposerContext();
  const [wordLimit, setWordLimit] = useState<number | undefined>();

  useEffect(() => {
    const updateWordLimit = () => {
      editor.getEditorState().read(() => {
        const node = findHeadingNodeByHeadingId(editor, headingId);
        if (node instanceof EnhancedHeadingNode) {
          setWordLimit(node.getWordLimit());
        }
      });
    };

    // Initial update
    updateWordLimit();

    // Listen for node changes
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, (node) => {
      if (node.getHeadingId() === headingId) {
        updateWordLimit();
      }
    });

    return () => {
      removeNodeTransform();
    };
  }, [editor, headingId]);

  if (!showLimit && wordCount === 0) {
    return null;
  }

  const getDisplayText = () => {
    if (wordLimit) {
      return `${wordCount}/${wordLimit} ${t('word_count', {count: wordLimit})}`;
    }
    return `${wordCount} ${t('word_count', {count: wordCount})}  `;
  };

  const getColor = () => {
    if (isOverLimit) return 'text-red-600';
    if (wordLimit && wordCount > wordLimit * 0.8) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <span
      className={`text-xs ${getColor()} ${className || ''}`}
      title={isOverLimit ? 'Word limit exceeded' : getDisplayText()}
      style={{
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      {getDisplayText()}
    </span>
  );
};
